import DashboardLayout from "@/components/DashboardLayout";
import { PageHeader } from "@/components/PageHeader";
import { ResourceCard } from "@/components/ResourceCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { trackEvent } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { Activity, Clock, FolderKey, Key, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function VirtualEnv() {
  const [, setLocation] = useLocation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [autoWhitelistFirstRequest, setAutoWhitelistFirstRequest] = useState(false);

  const { data: virtualEnvs, isLoading, refetch } = trpc.virtualEnv.list.useQuery();
  const createMutation = trpc.virtualEnv.create.useMutation({
    onSuccess: () => {
      trackEvent("virtual_env_created");
      toast.success("Virtual .env created successfully");
      setIsCreateOpen(false);
      setName("");
      setDescription("");
      setAutoWhitelistFirstRequest(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = () => {
    if (!name) {
      toast.error("Name is required");
      return;
    }

    createMutation.mutate({
      name,
      description: description || undefined,
      autoWhitelistFirstRequest,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Virtual .env"
          subtitle="Group multiple secrets under a single token"
          action={
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Virtual .env
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Virtual .env</DialogTitle>
                  <DialogDescription>
                    Create a new virtual environment to group multiple secrets together.
                    You can then fetch all secrets with a single token.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Production API Keys"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Optional description of this virtual environment"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="auto-whitelist">Auto-whitelist First Request</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically whitelist the IP address of the first access
                      </p>
                    </div>
                    <Switch
                      id="auto-whitelist"
                      checked={autoWhitelistFirstRequest}
                      onCheckedChange={setAutoWhitelistFirstRequest}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending}>
                    {createMutation.isPending ? "Creating..." : "Create Virtual .env"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="size-8 text-primary" />
          </div>
        ) : virtualEnvs && virtualEnvs.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {virtualEnvs.map((env) => (
              <ResourceCard
                key={env.id}
                icon={FolderKey}
                title={env.name}
                description={env.description ?? undefined}
                metadata={[
                  {
                    icon: Key,
                    label: `${env.secretCount || 0} secret${env.secretCount !== 1 ? "s" : ""}`,
                  },
                  {
                    icon: Activity,
                    label: `${env.activeTokenCount || 0} active token${env.activeTokenCount !== 1 ? "s" : ""}`,
                  },
                  ...(env.lastUsedAt
                    ? [{ icon: Clock, label: `Last used ${new Date(env.lastUsedAt).toLocaleDateString()}` }]
                    : []),
                ]}
                onClick={() => setLocation(`/virtual-env/${env.id}`)}
              />
            ))}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FolderKey />
              </EmptyMedia>
              <EmptyTitle>No virtual environments yet</EmptyTitle>
              <EmptyDescription>
                Create your first virtual .env to group multiple secrets together.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Virtual .env
              </Button>
            </EmptyContent>
          </Empty>
        )}
      </div>
    </DashboardLayout>
  );
}
